import { useEffect, useState } from 'react';
import i18n from '../../../i18n';
import { v1Dashboard, v2Phases, v2Dashboard, type V1Care } from '../../../lib/v1';
import { v2CalendarEvents } from '../../../lib/calendarEvents';
import type { DashboardData } from './types';

/**
 * Lädt die Daten der Dashboard-Widgets (einmal beim Mount). Geteilt zwischen
 * dem persönlichen Admin-Dashboard und dem Abteilungs-Dashboard.
 */
export function useDashboardData(): { data: DashboardData | null; dataError: string | null } {
  const [data, setData] = useState<DashboardData | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      // allSettled statt all: ein einzelner langsamer oder fehlschlagender
      // Endpunkt darf nicht das GANZE Dashboard leeren. Genau das passierte am
      // 18.09.2026 — die Anrufliste brauchte ~21 s, lief in den 15-s-Timeout
      // des axios-Clients, das Promise.all rejectete und alle sieben Widgets
      // blieben dauerhaft auf "Wird geladen…". Jetzt zeigt jedes Widget, was
      // es hat; nur die kaputten Teile bleiben leer.
      const [badges, newCaresPag, todayEvents, phaseCounts, callList] =
        await Promise.allSettled([
          v1Dashboard.badges(),
          v1Dashboard.newCares({ perPage: 5 }),
          v2CalendarEvents.list(todayStart.toISOString(), todayEnd.toISOString()),
          v2Phases.counts(),
          // Batch-13 — VBS-Dashboard: Anrufliste.
          v2Dashboard.callList({ per_page: 10 }),
        ]);
      if (!alive) return;

      const val = <T,>(r: PromiseSettledResult<T>, fallback: T): T =>
        r.status === 'fulfilled' ? r.value : fallback;

      setData({
        badges: val(badges, null as any),
        newCares: (val(newCaresPag, { data: [] } as any).data ?? []) as V1Care[],
        todayEvents: val(todayEvents, [] as any),
        phaseCounts: val(phaseCounts, null as any),
        callList: val(callList, { data: [] } as any).data ?? [],
      });

      // Nur melden, WELCHE Teile fehlen — nicht das ganze Dashboard abwürgen.
      // i18n.t() statt tr() aus dem Hook: so braucht der Effect `tr` nicht als
      // Dependency (exhaustive-deps) und läuft weiterhin nur einmal beim Mount.
      const namen = [
        i18n.t('dashboard.page.section.kpi'),
        i18n.t('betreuung.detail.section.assignments'),
        i18n.t('dashboard.page.section.termine'),
        i18n.t('dashboard.widget.pipeline.title'),
        i18n.t('dashboard.widget.call_list.title'),
      ];
      const fehl = [badges, newCaresPag, todayEvents, phaseCounts, callList]
        .map((r, i) => (r.status === 'rejected' ? namen[i] : null))
        .filter(Boolean);
      setDataError(fehl.length > 0 ? i18n.t('dashboard.page.load_error', { items: fehl.join(', ') }) : null);
    })();
    return () => { alive = false; };
  }, []);

  return { data, dataError };
}
